import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Tag } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { promoCodesService } from '../services/api'

const PromoCodeForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    used_count: 0,
    is_active: true,
    valid_from: '',
    valid_until: ''
  })

  const [errors, setErrors] = useState({})

  // Validation function
  const validateForm = () => {
    const newErrors = {}

    // Code validation
    if (!promoForm.code.trim()) {
      newErrors.code = 'رمز الخصم مطلوب'
    } else if (promoForm.code.length < 3) {
      newErrors.code = 'رمز الخصم يجب أن يكون على الأقل 3 أحرف'
    } else if (!/^[A-Z0-9]+$/.test(promoForm.code)) {
      newErrors.code = 'رمز الخصم يجب أن يحتوي على أحرف كبيرة وأرقام فقط'
    }

    // Description validation
    if (!promoForm.description.trim()) {
      newErrors.description = 'وصف كوبون الخصم مطلوب'
    }

    // Discount value validation
    if (!promoForm.discount_value || promoForm.discount_value <= 0) {
      newErrors.discount_value = 'قيمة الخصم مطلوبة ويجب أن تكون أكبر من صفر'
    } else if (promoForm.discount_type === 'percentage' && promoForm.discount_value > 100) {
      newErrors.discount_value = 'نسبة الخصم يجب أن تكون أقل من أو تساوي 100%'
    }

    // Min order amount validation (optional)
    if (promoForm.min_order_amount && promoForm.min_order_amount < 0) {
      newErrors.min_order_amount = 'الحد الأدنى لمبلغ الطلب يجب أن يكون أكبر من أو يساوي صفر'
    }

    // Max uses validation (optional)
    if (promoForm.max_uses && promoForm.max_uses <= 0) {
      newErrors.max_uses = 'الحد الأقصى للاستخدام يجب أن يكون أكبر من صفر'
    }

    // Date validation
    if (promoForm.valid_from && promoForm.valid_until) {
      const fromDate = new Date(promoForm.valid_from)
      const untilDate = new Date(promoForm.valid_until)
      if (fromDate >= untilDate) {
        newErrors.valid_until = 'تاريخ انتهاء الصلاحية يجب أن يكون بعد تاريخ البداية'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Fetch promo code data if editing
  const { data: promoCode, isLoading: promoLoading } = useQuery({
    queryKey: ['promoCode', id],
    queryFn: () => promoCodesService.getPromoCode(id),
    enabled: !!id,
    retry: false
  })

  // Create promo code mutation
  const createPromoMutation = useMutation({
    mutationFn: (promoData) => promoCodesService.createPromoCode(promoData),
    onSuccess: () => {
      queryClient.invalidateQueries(['promoCodes'])
      navigate('/admin', { state: { activeTab: 'promoCodes' } })
    },
    onError: (error) => {
      console.error('Create promo code error:', error)
      const errorData = error.response?.data
      
      // Handle specific error types
      if (errorData?.error === 'Promo code already exists') {
        setErrors({ code: 'كود الخصم موجود بالفعل، يرجى استخدام كود مختلف (مثل: ' + promoData.code + new Date().getTime().toString().slice(-3) + ')' })
      } else {
        setErrors({ submit: 'فشل في إنشاء كوبون الخصم: ' + (errorData?.error || errorData?.message || error.message) })
      }
    }
  })

  // Update promo code mutation
  const updatePromoMutation = useMutation({
    mutationFn: ({ promoId, promoData }) => promoCodesService.updatePromoCode(promoId, promoData),
    onSuccess: () => {
      queryClient.invalidateQueries(['promoCodes'])
      queryClient.invalidateQueries(['promoCode', id])
      navigate('/admin', { state: { activeTab: 'promoCodes' } })
    },
    onError: (error) => {
      console.error('Update promo code error:', error)
      const errorData = error.response?.data
      
      // Handle specific error types
      if (errorData?.error === 'Promo code already exists') {
        setErrors({ code: 'كود الخصم موجود بالفعل، يرجى استخدام كود مختلف' })
      } else {
        setErrors({ submit: 'فشل في تحديث كوبون الخصم: ' + (errorData?.error || errorData?.message || error.message) })
      }
    }
  })

  // Load promo code data when editing
  useEffect(() => {
    if (promoCode) {
      setPromoForm({
        code: promoCode.code || '',
        description: promoCode.description || '',
        discount_type: promoCode.discount_type || 'percentage',
        discount_value: promoCode.discount_value || '',
        min_order_amount: promoCode.min_order_amount || '',
        max_uses: promoCode.max_uses || '',
        used_count: promoCode.used_count || 0,
        is_active: promoCode.is_active !== false,
        valid_from: promoCode.valid_from ? promoCode.valid_from.split('T')[0] : '',
        valid_until: promoCode.valid_until ? promoCode.valid_until.split('T')[0] : ''
      })
    }
  }, [promoCode])

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    // Validate form
    if (!validateForm()) {
      return
    }

    // Prepare data for submission
    const submitData = {
      ...promoForm,
      discount_value: parseFloat(promoForm.discount_value),
      min_order_amount: promoForm.min_order_amount ? parseFloat(promoForm.min_order_amount) : null,
      max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
      valid_from: promoForm.valid_from || null,
      valid_until: promoForm.valid_until || null
    }

    if (isEdit) {
      updatePromoMutation.mutate({ promoId: id, promoData: submitData })
    } else {
      createPromoMutation.mutate(submitData)
    }
  }

  const handleInputChange = (field, value) => {
    setPromoForm(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
    // Clear submit error when user starts making changes
    if (errors.submit) {
      setErrors(prev => ({ ...prev, submit: null }))
    }
  }

  if (promoLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/admin', { state: { activeTab: 'promoCodes' } })}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-2">
                  <Tag className="w-6 h-6 text-blue-600" />
                  <h1 className="text-xl font-semibold text-gray-900">
                    {isEdit ? 'تعديل كوبون الخصم' : 'إضافة كوبون خصم جديد'}
                  </h1>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رمز الخصم <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={promoForm.code}
                  onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.code ? 'border-red-500' : ''}`}
                  required
                  placeholder="مثال: SAVE20"
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نوع الخصم
                </label>
                <select
                  value={promoForm.discount_type}
                  onChange={(e) => handleInputChange('discount_type', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="percentage">نسبة مئوية (%)</option>
                  <option value="fixed">مبلغ ثابت</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  وصف كوبون الخصم <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={promoForm.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.description ? 'border-red-500' : ''}`}
                  rows={3}
                  required
                  placeholder="وصف كوبون الخصم..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  قيمة الخصم <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={promoForm.discount_type === 'percentage' ? '100' : undefined}
                  value={promoForm.discount_value}
                  onChange={(e) => handleInputChange('discount_value', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.discount_value ? 'border-red-500' : ''}`}
                  required
                  placeholder={promoForm.discount_type === 'percentage' ? '20' : '50'}
                />
                {errors.discount_value && (
                  <p className="mt-1 text-sm text-red-600">{errors.discount_value}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحد الأدنى لمبلغ الطلب
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={promoForm.min_order_amount}
                  onChange={(e) => handleInputChange('min_order_amount', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.min_order_amount ? 'border-red-500' : ''}`}
                  placeholder="100"
                />
                {errors.min_order_amount && (
                  <p className="mt-1 text-sm text-red-600">{errors.min_order_amount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحد الأقصى للاستخدام
                </label>
                <input
                  type="number"
                  min="1"
                  value={promoForm.max_uses}
                  onChange={(e) => handleInputChange('max_uses', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.max_uses ? 'border-red-500' : ''}`}
                  placeholder="100"
                />
                {errors.max_uses && (
                  <p className="mt-1 text-sm text-red-600">{errors.max_uses}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  تاريخ البداية
                </label>
                <input
                  type="date"
                  value={promoForm.valid_from}
                  onChange={(e) => handleInputChange('valid_from', e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  تاريخ انتهاء الصلاحية
                </label>
                <input
                  type="date"
                  value={promoForm.valid_until}
                  onChange={(e) => handleInputChange('valid_until', e.target.value)}
                  className={`w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${errors.valid_until ? 'border-red-500' : ''}`}
                />
                {errors.valid_until && (
                  <p className="mt-1 text-sm text-red-600">{errors.valid_until}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الحالة
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={promoForm.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    نشط
                  </label>
                </div>
              </div>

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    عدد مرات الاستخدام
                  </label>
                  <input
                    type="number"
                    value={promoForm.used_count}
                    readOnly
                    className="w-full border-gray-300 rounded-md shadow-sm bg-gray-50"
                  />
                </div>
              )}
            </div>

            {/* Submit Error Display */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin', { state: { activeTab: 'promoCodes' } })}
                className="btn btn-outline"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={createPromoMutation.isLoading || updatePromoMutation.isLoading}
                className="btn btn-primary"
              >
                <Save className="w-4 h-4 mr-2" />
                {(createPromoMutation.isLoading || updatePromoMutation.isLoading) ? 'حفظ...' : 'حفظ'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default PromoCodeForm
